import React from 'react';
import { TrendingUp, Calendar, Wallet, CheckCircle, Lock, Ruler, Clock } from 'lucide-react';
import { incomeData } from '../../data/mock';
import Reveal from './Reveal';

const IncomeSection = () => {
  const terms = [
    { icon: Wallet, value: incomeData.leasePrice, label: incomeData.perUnit },
    { icon: Ruler, value: incomeData.minArea, label: 'Minimum area' },
    { icon: Clock, value: incomeData.maxTenure, label: 'Maximum period' },
  ];

  return (
    <section
      id="income"
      className="relative py-20 md:py-28 bg-[#00382a] overflow-hidden"
      data-testid="income-section"
    >
      {/* Ambient glow accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#c4e538]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#0C6951]/30 blur-3xl" />

      <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
        {/* Header */}
        <Reveal className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm px-5 py-2 rounded-full mb-5">
            <TrendingUp className="w-4 h-4 text-[#c4e538]" />
            <span className="text-white text-sm font-bold tracking-wide">The Opportunity</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-4">
            <span className="text-[#c4e538]">2.5X</span> in 30 Months
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Choose the payout plan that fits you. Both are illustrated on a ₹1,00,000 lease value.
          </p>
        </Reveal>

        {/* Lease terms */}
        <Reveal className="mb-12" delay={80}>
          <div className="grid grid-cols-3 max-w-3xl mx-auto rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-sm">
            {terms.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className={`text-center py-6 px-3 ${i < terms.length - 1 ? 'border-r border-white/10' : ''}`}>
                  <Icon className="w-5 h-5 text-[#c4e538] mx-auto mb-2" />
                  <p className="text-white text-xl md:text-3xl font-black leading-none">{t.value}</p>
                  <p className="text-white/60 text-xs md:text-sm mt-2">{t.label}</p>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Two plans */}
        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto items-stretch">
          {/* Plan 1 - Phased income */}
          <Reveal className="h-full">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl h-full flex flex-col">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#004534]/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#004534]" />
                  </div>
                  <div>
                    <h3 className="text-[#004534] font-black text-xl">{incomeData.plan36.title}</h3>
                    <p className="text-[#0C6951] text-sm font-semibold">{incomeData.plan36.subtitle}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 flex-grow flex flex-col">
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {incomeData.plan36.schedule.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 bg-[#f8fdf5] rounded-lg">
                      <span className="text-gray-500 text-xs">{item.period}</span>
                      <span className="text-[#004534] font-bold text-sm">{item.amount}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto border-t-2 border-dashed border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-semibold">Total income</span>
                    <span className="text-[#004534] font-black text-3xl">{incomeData.plan36.totalPayout}</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Plan 2 - One-time settlement (highlighted) */}
          <Reveal className="h-full" delay={90}>
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl ring-2 ring-[#c4e538] h-full flex flex-col">
              <div className="bg-[#c4e538] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#004534] rounded-xl flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-[#c4e538]" />
                  </div>
                  <div>
                    <h3 className="text-[#004534] font-black text-xl">{incomeData.plan42.title}</h3>
                    <p className="text-[#004534]/70 text-sm font-semibold">{incomeData.plan42.subtitle}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 bg-[#004534] text-[#c4e538] px-3 py-1 rounded-full text-xs font-bold">
                  <TrendingUp className="w-3 h-3" /> BEST VALUE
                </span>
              </div>

              <div className="p-6 flex-grow flex flex-col items-center justify-center text-center">
                <p className="text-gray-500 text-sm mb-5">{incomeData.plan42.note}</p>

                <div className="w-full bg-gradient-to-br from-[#f8fdf5] to-[#eef8e4] rounded-2xl p-8 border border-[#c4e538]/40 mb-5">
                  <p className="text-6xl md:text-7xl font-black text-[#004534] leading-none mb-3">
                    {incomeData.plan42.amount}
                  </p>
                  <p className="text-[#0C6951] font-bold text-lg">
                    {incomeData.plan42.multiplier} {incomeData.plan42.duration}
                  </p>
                </div>

                <div className="bg-[#004534] inline-flex px-5 py-2 rounded-full">
                  <span className="text-white text-sm font-medium">
                    Single payout at <strong className="text-[#c4e538]">30 months</strong>
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Plan rules */}
        <Reveal className="mt-10 max-w-3xl mx-auto" delay={60}>
          <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#c4e538]" />
              <h4 className="font-bold text-white text-base">Plan Rules</h4>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Plan determines your lease duration',
                'Payouts are proportional to lease value',
                'Select your plan at lease start',
                'Plan cannot be changed later',
              ].map((rule, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#c4e538] flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-sm">{rule}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Disclaimer */}
        <p className="text-center text-white/40 text-xs mt-6 max-w-2xl mx-auto leading-relaxed">
          {incomeData.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default IncomeSection;
